import React, { useState } from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import { useGetAllUsersQuery, useAddUserMutation, useSendInvitationMutation } from '../../services/api';
import useSelectedCompany from '../../hooks/useSelectedCompany';
import useCompanyOptions from '../../hooks/useCompanyOptions';
import AllUserTable from './AllUsersTable';
import AllUsersHeader from './AllUsersHeader';
import ErrorSnackbar from '../../components/ErrorSnackbar';
import AddUserDialogBox from './AddUserDialogBox';

const AllUsers = () => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { companyOptions } = useCompanyOptions();
    const { selectedCompany, selectedCompanyId, handleCompanyChange, getSelectedCompanyLabel } = useSelectedCompany({ companyOptions });

    const { data: usersData, refetch: refetchUsers } = useGetAllUsersQuery(selectedCompanyId || '', { skip: !selectedCompanyId });
    const [addUserMutation] = useAddUserMutation();
    const [sendInvitation] = useSendInvitationMutation();

    const [userData, setUserData] = useState({ name: '', email: '', country: '', phoneNumber: '', role: '', company: selectedCompany });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
    const [openDialog, setOpenDialog] = useState(false);

    const handleOpenDialog = () => setOpenDialog(true);
    const handleCloseDialog = () => setOpenDialog(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setUserData({
            ...userData,
            [name]: value,
        });
    };

    const handleAddUser = async () => {
        try {
            if (!userData.name || !userData.email || !userData.role) {
                showSnackbar('Please fill all fields', 'error');
            } else {
                const addUserResponse = await addUserMutation({
                    name: userData.name,
                    email: userData.email,
                    role: userData.role,
                    company: userData.company,
                });

                if (addUserResponse.error) {
                    showSnackbar('User already exists', 'error');
                } else {
                    await sendInvitation({
                        name: userData.name,
                        email: userData.email,
                    });

                    showSnackbar('User added successfully', 'success');
                    refetchUsers();
                }
            }
        } catch (error) {
            console.error('Error adding user:', error);
            showSnackbar('Failed to add user', 'error');
        } finally {
            handleCloseDialog();
        }
    };

    const showSnackbar = (message, severity) => {
        setSnackbar({ open: true, message, severity });
    };

    return (
        <Box m={isSmallScreen ? '1rem' : '1rem 2.5rem'}>
            <AllUsersHeader
                selectedCompany={selectedCompany}
                handleCompanyChange={handleCompanyChange}
                companyOptions={companyOptions}
                handleOpenDialog={handleOpenDialog}
                isSmallScreen={isSmallScreen}
                getSelectedCompanyLabel={getSelectedCompanyLabel()}
            />
            {usersData && (
                <Box marginTop="1rem">
                    <AllUserTable userData={usersData} refetch={refetchUsers} isSmallScreen={isSmallScreen} />
                </Box>
            )}
            <AddUserDialogBox
                open={openDialog}
                handleCloseDialog={handleCloseDialog}
                userData={userData}
                handleInputChange={handleInputChange}
                handleAddUser={handleAddUser}
                companyOptions={companyOptions}
            />
            <ErrorSnackbar open={snackbar.open} message={snackbar.message} severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} />
        </Box>
    );
};

export default AllUsers;
